/**
 * Pure, dependency-free bounds/coordinate helpers for spawn points (SC-393).
 * No React, Colyseus, or Parse SDK types in any signature here — {x,y}-shaped data in,
 * {x,y}-shaped data out — so this module can be imported unmodified from both the client and
 * server bundles via plain relative paths, the same repo-root-`types/` pattern already used by
 * `types/Messages.ts` (the client/server message contract). Lives here, not under `client/src`,
 * specifically so server code importing it isn't reaching into the client bundle.
 *
 * Tile size and the tile->world-pixel convention here match the existing, already-live
 * CustomRoom spawn point conversion (`spawnX = tileX * 32`, see Game.ts's spawn resolution) —
 * not a tile-center offset. Keeping this consistent matters: this codebase already treats a
 * spawn tile's world position as its top-left corner (`tileX * TILE_SIZE`), the same as
 * VirtualRoomAreaService's rendering and tile-membership math.
 */

export const SPAWN_POINT_TILE_SIZE_PX = 32

export type Tile = { x: number; y: number }
export type WorldPoint = { x: number; y: number }

export type TileBounds = {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/**
 * A grid the caller adapts from whatever collision source it has (Phaser tilemap layers on
 * the client, a parsed tileset JSON in the backfill script). Framework-free by design: this
 * module knows nothing about how walkability is determined, only how to ask.
 */
export type WalkabilityGrid = {
  isTileWalkable: (tileX: number, tileY: number) => boolean
}

/**
 * Computes the tile-space bounding box (and its tile-space center) of a set of tiles.
 * Mirrors VirtualRoomAreaService.ts's bounds accumulation (min/max over tileX/tileY) so
 * room bounds computed here agree with what the painter already computes.
 */
export function computeBounds(tiles: Tile[]): TileBounds & { center: Tile } {
  if (tiles.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, center: { x: 0, y: 0 } }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const tile of tiles) {
    if (tile.x < minX) minX = tile.x
    if (tile.y < minY) minY = tile.y
    if (tile.x > maxX) maxX = tile.x
    if (tile.y > maxY) maxY = tile.y
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 }
  }
}

/** True when a tile lies strictly inside a tile-space bounding box (inclusive of edges). */
export function isTileWithinBounds(tile: Tile, bounds: TileBounds): boolean {
  return tile.x >= bounds.minX && tile.x <= bounds.maxX && tile.y >= bounds.minY && tile.y <= bounds.maxY
}

/**
 * Chebyshev (max-axis) distance in tiles from a tile to the nearest edge of a bounding box —
 * 0 when the tile is inside or on the boundary. Used for the "must be outside the room but
 * within a few tiles of it" placement validation.
 */
export function tileDistanceFromBounds(tile: Tile, bounds: TileBounds): number {
  const dx = Math.max(bounds.minX - tile.x, 0, tile.x - bounds.maxX)
  const dy = Math.max(bounds.minY - tile.y, 0, tile.y - bounds.maxY)
  return Math.max(dx, dy)
}

/** Delegates to the caller-supplied grid; permissive (true) when no grid is available. */
export function isWalkable(tileset: WalkabilityGrid | null | undefined, x: number, y: number): boolean {
  if (!tileset) return true
  return tileset.isTileWalkable(x, y)
}

/**
 * Converts a stored SpawnPoint.tile value to the world-pixel coordinate every consumer
 * (editor preview, server hydration, Phase 3's teleport resolver) must use as a destination.
 * Nothing downstream should ever read SpawnPoint.tile directly as x/y — an earlier draft of
 * this feature skipped this and landed players at roughly 1/32 of the intended location.
 */
export function tileToWorldCenter(tile: Tile): WorldPoint {
  return { x: tile.x * SPAWN_POINT_TILE_SIZE_PX, y: tile.y * SPAWN_POINT_TILE_SIZE_PX }
}
